import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useApiQuery } from '@/modules/common';
import { FileObject } from '@/modules/common/types';
import { Routes } from '@/router/routes';

import {
  AIConfigurationTab,
  CollarTab,
  InfoTab,
  InventoryTab,
  LacesTab,
  MasksTab,
  ModelsTab,
  OrdersHistoryTab,
  ParticipantsTab,
  PrintersTab,
  SmsHistoryTab,
  StylesTab,
  VideoTab,
} from '../components';
import { EventDetailsTab } from '../constants';
import {
  EventErrorMessages,
  EventSystemPromptResponse,
  EventType,
} from '../types';

export const useEventDetailsTabs = ({
  selectedTypes,
  eventSystemPrompt,
  errorMessages,
  videoFileObject,
  idleStateVideoFileObject,
  backgroundImageFileObject,
}: {
  selectedTypes: EventType[];
  eventSystemPrompt: EventSystemPromptResponse | null;
  errorMessages?: EventErrorMessages | null;
  videoFileObject?: FileObject | null;
  idleStateVideoFileObject?: FileObject | null;
  backgroundImageFileObject?: FileObject | null;
}) => {
  const navigate = useNavigate();
  const params = useParams<{ id: string; tab: EventDetailsTab }>();

  const { isFetching: isFetchingTypes } = useApiQuery<EventType[]>(
    `/events/types`,
    {
      queryKey: ['eventTypes'],
      initialData: null,
    }
  );

  const visibleTabs = useMemo(
    () => [
      ...new Set([
        ...new Set(
          selectedTypes?.flatMap((type) => {
            return Object.keys(type.requiredFields);
          })
        ),
        EventDetailsTab.Info,
      ]),
    ],
    [selectedTypes]
  );

  const onChangeTab = (tab: EventDetailsTab) => {
    navigate(`${Routes.Events}/${params.id}/${tab}`);
  };

  const tabsConfig = useMemo(
    () => [
      {
        value: EventDetailsTab.Info,
        label: 'Info',
        title: 'Basic Info',
        Component: InfoTab,
        componentProps: { errorMessages },
      },
      {
        value: EventDetailsTab.AIConfiguration,
        label: 'AI Configuration',
        title: 'Models',
        Component: AIConfigurationTab,
        componentProps: { eventSystemPrompt },
      },
      {
        value: EventDetailsTab.Video,
        label: 'Video',
        noWrapper: true,
        Component: VideoTab,
        componentProps: {
          videoFileObject,
          idleStateVideoFileObject,
          backgroundImageFileObject,
        },
      },
      {
        value: EventDetailsTab.Models,
        label: 'Models',
        Component: ModelsTab,
      },
      {
        value: EventDetailsTab.LacesColors,
        label: 'Laces Colors',
        Component: LacesTab,
      },
      {
        value: EventDetailsTab.CollarColors,
        label: 'Liner Colors',
        Component: CollarTab,
      },
      {
        value: EventDetailsTab.Styles,
        label: 'Styles',
        Component: StylesTab,
      },
      {
        value: EventDetailsTab.Masks,
        label: 'Masks',
        Component: MasksTab,
      },
      {
        value: EventDetailsTab.Printers,
        label: 'Printers',
        Component: PrintersTab,
      },
      {
        value: EventDetailsTab.Participants,
        label: 'Participants',
        Component: ParticipantsTab,
      },
      {
        value: EventDetailsTab.Inventory,
        label: 'Inventory',
        Component: InventoryTab,
      },
      {
        value: EventDetailsTab.OrdersHistory,
        label: 'Orders History',
        Component: OrdersHistoryTab,
      },
      {
        value: EventDetailsTab.SMSHistory,
        label: 'SMS History',
        Component: SmsHistoryTab,
      },
    ],
    [
      eventSystemPrompt,
      errorMessages,
      videoFileObject,
      idleStateVideoFileObject,
      backgroundImageFileObject,
    ]
  );

  // if tab is not visible, redirect to first visible tab
  useEffect(() => {
    if (isFetchingTypes) {
      return;
    }

    if (!visibleTabs.includes(params.tab)) {
      navigate(`${Routes.Events}/${params.id}/${visibleTabs[0]}`);
    }
  }, [visibleTabs, params.tab, isFetchingTypes]);

  return {
    tabsConfig,
    visibleTabs,
    activeTab: params.tab,
    isFetchingTypes,
    onChangeTab,
    onBack: () => navigate(Routes.Events),
  };
};
